import type { FirebaseClient, FirebaseClientConfig } from '@akpl/firebase/client';
import { AkpError, type PublicConfig } from '@akpl/shared';

import { analytics } from '../analytics';
import { api, APP_VERSION, BOOT_CONFIG_BUDGET, setTokenProvider } from '../api';
import { type BridgeHandle, getBridge } from '../bridge';
import { loadFirebase, prefetchFirebase } from '../firebase';
import { initAppI18n, resolveLocale } from '../i18n';
import { prefetchHome } from '../queryClient';
import type { TvStoreApi } from '../store';

export interface BootDeps {
  store: TvStoreApi;
  fetchConfig?: () => Promise<PublicConfig>;
  loadFirebase?: (
    config: FirebaseClientConfig,
    bridge: BridgeHandle['kind'],
  ) => Promise<FirebaseClient | null>;
  bridge?: BridgeHandle;
  initI18n?: () => Promise<unknown>;
}

/**
 * Boot sequence (docs/04 §1):
 * 1. bridge detection (native or mock) → device id
 * 2. i18n (persisted override → navigator.language)
 * 3. `GET /config` → Mixpanel stub
 * 4. Firebase (lazy chunk) → resume when a persisted TV session is still active (`GET /me`),
 *    or "offline auth" mode when `/config` has no Firebase project.
 *
 * Steps 1 and 4 are the power-cycle contract of docs/04 §1.3: the device id is persisted on
 * both hosts, so a reload — or a stick that was unplugged — resumes the session it left. The
 * one way out is `?fresh=1`, which mints a new device id in the browser bridge; the persisted
 * user belongs to the previous TV, so it is signed out here before auth is even consulted.
 */
export async function runBoot({
  store,
  fetchConfig = () => api.config(BOOT_CONFIG_BUDGET),
  loadFirebase: load = loadFirebase,
  bridge = getBridge(APP_VERSION),
  initI18n = () => initAppI18n(),
}: BootDeps): Promise<void> {
  const s = store.getState();
  s.setBoot('booting');
  s.setDevice({ id: bridge.deviceId, bridge: bridge.kind, info: bridge.info });
  s.setLocale(resolveLocale());
  // The Firebase chunk does not depend on `/config`; only initialising it does (UX-110).
  prefetchFirebase();

  try {
    const [config] = await Promise.all([fetchConfig(), initI18n()]);
    store.getState().setConfig(config);
    analytics.init(config.mixpanelToken, { env: config.env, app_version: APP_VERSION });

    // Both hosts persist the session, because both persist the device id (`persistenceFor`).
    const firebase = await load(config.firebase, bridge.kind).catch((err: unknown) => {
      bridge.bridge.log('error', `firebase init failed: ${String(err)}`);
      return null;
    });

    if (!firebase) {
      store.getState().setAuthMode('offline');
      store.getState().setAuth({ status: 'signed_out' });
    } else {
      store.getState().setAuthMode('firebase');
      setTokenProvider(() => firebase.getIdToken());
      // `?fresh=1`: this is a different TV now, so the persisted user must not resume.
      if (bridge.fresh) {
        await firebase.signOut().catch(() => {});
      }
      const user = await firebase.waitForAuth();
      if (!user) {
        store.getState().setAuth({ status: 'signed_out' });
      } else {
        await resumeSession(store, user.uid, bridge);
      }
    }
    store.getState().setBoot('ready');
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // UX-373: the code is what the screen can say in Spanish; the message is the diagnostic.
    const code = AkpError.is(err) ? err.code : 'internal';
    bridge.bridge.log('error', `boot failed: ${message}`);
    store.getState().setBoot('error', message, code);
  }
}

/** A persisted Firebase user resumes only when `/me` still reports an active session. */
async function resumeSession(store: TvStoreApi, uid: string, bridge: BridgeHandle): Promise<void> {
  try {
    const me = await api.me();
    if (me.activeSession) {
      const session = await api.session({ params: { sid: me.activeSession.id } });
      store.getState().setAuth({
        status: 'signed_in',
        uid,
        user: {
          id: me.user.id,
          displayName: me.user.displayName,
          photoUrl: me.user.photoUrl,
          isAnonymous: me.user.isAnonymous,
        },
        features: me.features,
      });
      store.getState().setSession(session.session);
      // A resumed session lands straight on Home: start its request now (UX-110).
      prefetchHome();
      analytics.identify(uid);
      return;
    }
  } catch (err) {
    bridge.bridge.log('warn', `resume failed: ${String(err)}`);
  }
  store.getState().setAuth({ status: 'signed_out', uid: null, user: null, features: [] });
}
